import { z } from 'zod';

export const paginationInputSchema = z.object({
  page: z.coerce.number().int().min(1).default(1),
  pageSize: z.coerce.number().int().min(1).max(200).default(25),
});

export const sortDirectionSchema = z.enum(['asc', 'desc']);

export type SortDirection = z.infer<typeof sortDirectionSchema>;

/**
 * The shape every sortable list endpoint takes. `sortBy` is widened to a string
 * here and narrowed to the resource's own columns by `sortInput`; a route
 * declares the narrowed one, never this.
 */
export const sortInputSchema = z.object({
  sortBy: z.string().optional(),
  sortDir: sortDirectionSchema.default('desc'),
});

/**
 * The sortable columns of one resource, as an enum. A column name the client
 * invents fails validation at the boundary, so it never reaches a repository
 * and can never reach SQL — the whitelist is the contract, and the repository's
 * order map is keyed by the same union so a missing entry is a compile error.
 */
export function sortInput<const Fields extends readonly [string, ...string[]]>(
  fields: Fields,
  defaultField: Fields[number],
  defaultDirection: SortDirection,
) {
  return sortInputSchema.extend({
    sortBy: z.enum(fields).default(defaultField),
    sortDir: sortDirectionSchema.default(defaultDirection),
  });
}

export function paginated<T extends z.ZodType>(item: T) {
  return z.object({
    items: z.array(item),
    total: z.number().int().min(0),
    page: z.number().int().min(1),
    pageSize: z.number().int().min(1),
  });
}

export type PaginationInput = z.infer<typeof paginationInputSchema>;
